/**
 * Reusable two-pane browser for the synced Freshdesk Solutions knowledge base.
 *
 * Portal (ANZ/USA) selector up top; an always-expanded, filterable category/folder
 * tree on the left that opens on an "All articles" item; a scope-aware searchable
 * article list on the right. One component, two modes:
 *  - `browse`  - inline on the Freshdesk integration "Browse" tab (open-in-Freshdesk).
 *  - `picker`  - inside `SolutionsBrowserDialog`; a row click fires `onSelect` and the
 *                dialog closes.
 *
 * Reads the cached aggregate (`GET /admin/freshdesk/solutions`) - no live FD call,
 * no new endpoint. Search is client-side over title / folder / category.
 */
import React, { useMemo, useState } from 'react'
import {
  Box,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

import { useDataFetching } from '../../../hooks/useDataFetching'
import {
  FreshdeskSolutionArticle,
  FreshdeskSolutions,
  integrationsApi,
} from '../../../services/admin/integrationsApi'
import SegmentedSelector from '../../admin/shared/fields/SegmentedSelector'
import DataFetchWrapper from '../../inspector/shared/DataFetchWrapper'
import { buildSolutionsTree } from './solutionsTree'
import { categoryFolder, openLink, statusChip } from './freshdeskArticleHelpers'

type Market = 'ANZ' | 'USA'

/** Right-pane scope, driven by the left selection. */
type Scope =
  | { type: 'all' }
  | { type: 'category'; id: number }
  | { type: 'folder'; id: number }

interface Props {
  mode: 'browse' | 'picker'
  /** Initial portal; defaults to ANZ. */
  defaultMarket?: Market
  /** Article id to mark as the current selection (picker mode). */
  selectedArticleId?: string
  /** Picker callback - fired on a row click. */
  onSelect?: (article: FreshdeskSolutionArticle) => void
  /** Pre-fetched tree (e.g. from the Solutions tab) to skip the self-fetch. */
  solutions?: FreshdeskSolutions
}

const PORTAL_OPTIONS = [
  { label: 'ANZ', value: 'ANZ' as Market },
  { label: 'USA', value: 'USA' as Market },
]

const matches = (haystack: string, query: string) =>
  haystack.toLowerCase().includes(query.trim().toLowerCase())

const SolutionsBrowser: React.FC<Props> = ({
  mode,
  defaultMarket,
  selectedArticleId,
  onSelect,
  solutions,
}) => {
  // Reuse a pre-fetched tree when given (inline tab); otherwise self-fetch (dialog).
  const usingProp = solutions != null
  const { data: fetched, loading, error, refetch } = useDataFetching<FreshdeskSolutions>({
    fetchFn: () =>
      usingProp
        ? Promise.resolve(solutions as FreshdeskSolutions)
        : integrationsApi.getFreshdeskSolutions(),
    dependencies: [usingProp],
  })
  const data = solutions ?? fetched

  const [market, setMarket] = useState<Market>(defaultMarket ?? 'ANZ')
  const [treeFilter, setTreeFilter] = useState('')
  const [scope, setScope] = useState<Scope>({ type: 'all' })
  const [articleFilter, setArticleFilter] = useState('')

  const categories = data?.categories ?? []
  const folders = data?.folders ?? []
  const articles = data?.articles ?? []

  // Left tree (market-scoped category -> folder list), narrowed by the filter box.
  const tree = useMemo(
    () => buildSolutionsTree(categories, folders, market, 'both'),
    [categories, folders, market],
  )
  const visibleTree = useMemo(
    () => (treeFilter.trim() ? tree.filter((n) => matches(n.searchText, treeFilter)) : tree),
    [tree, treeFilter],
  )

  // Article counts for the tree rows.
  const countByNode = useMemo(() => {
    const m = new Map<string, number>()
    for (const c of categories) {
      if (c.market === market) m.set(`category-${c.category_id}`, c.article_count)
    }
    for (const f of folders) {
      if (f.market === market) m.set(`folder-${f.folder_id}`, f.article_count)
    }
    return m
  }, [categories, folders, market])

  // Right pane: scope by the selected node, then filter by the article search box.
  const scopedArticles = useMemo(
    () => articles.filter((a) => a.market === market),
    [articles, market],
  )
  const inScope = useMemo(() => {
    if (scope.type === 'all') return scopedArticles
    if (scope.type === 'category') {
      return scopedArticles.filter((a) => a.category_id === scope.id)
    }
    return scopedArticles.filter((a) => a.folder_id === scope.id)
  }, [scopedArticles, scope])
  const shownArticles = useMemo(
    () =>
      articleFilter.trim()
        ? inScope.filter((a) =>
            matches(`${a.title} ${a.folder ?? ''} ${a.category ?? ''}`, articleFilter),
          )
        : inScope,
    [inScope, articleFilter],
  )

  const heading = useMemo((): React.ReactNode => {
    if (scope.type === 'all') return 'All articles'
    if (scope.type === 'category') {
      return (
        categories.find((c) => c.category_id === scope.id && c.market === market)?.name ??
        'Category'
      )
    }
    const f = folders.find((x) => x.folder_id === scope.id && x.market === market)
    return f ? categoryFolder(f.category, f.name) : 'Folder'
  }, [scope, categories, folders, market])

  const changeMarket = (next: Market) => {
    setMarket(next)
    setScope({ type: 'all' })
    setArticleFilter('')
    setTreeFilter('')
  }

  // Show the Category > Folder line only in the All-articles view; once you have
  // filtered to a category or folder on the left, it is redundant.
  const showBreadcrumb = scope.type === 'all'

  const articleRow = (a: FreshdeskSolutionArticle) => {
    const current = mode === 'picker' && a.article_id === selectedArticleId
    const body = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            {current && <CheckCircleIcon fontSize="small" color="success" />}
            <Typography variant="body2" noWrap>
              {a.title}
            </Typography>
          </Box>
          {showBreadcrumb && (a.category || a.folder) && (
            <Box sx={{ mt: 0.25 }}>
              {categoryFolder(a.category, a.folder, { icons: true })}
            </Box>
          )}
        </Box>
        {statusChip(a.status)}
      </Box>
    )
    return (
      <ListItem
        key={`${a.article_id}-${a.market}`}
        disablePadding
        divider
        secondaryAction={openLink(a.url)}
      >
        {mode === 'picker' ? (
          <ListItemButton selected={current} onClick={() => onSelect?.(a)} sx={{ pr: 7 }}>
            {body}
          </ListItemButton>
        ) : (
          <Box sx={{ width: '100%', px: 2, py: 1, pr: 7 }}>{body}</Box>
        )}
      </ListItem>
    )
  }

  return (
    <DataFetchWrapper
      loading={loading && !data}
      error={error}
      data={data}
      onRetry={refetch}
      emptyType="no-data"
      emptyTitle="No Solutions data"
      emptyDescription="Run a Freshdesk Solutions sync to populate the knowledge base."
    >
      <Box>
        <Box sx={{ mb: 2 }}>
          <SegmentedSelector
            ariaLabel="portal"
            options={PORTAL_OPTIONS}
            value={market}
            onChange={changeMarket}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'flex-start',
          }}
        >
          {/* Left: All articles + category/folder tree */}
          <Paper
            variant="outlined"
            sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0, alignSelf: { md: 'stretch' } }}
          >
            <Box sx={{ p: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Filter categories…"
                value={treeFilter}
                onChange={(e) => setTreeFilter(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <List dense sx={{ maxHeight: 520, overflow: 'auto', pt: 0 }}>
              <ListItemButton
                selected={scope.type === 'all'}
                onClick={() => setScope({ type: 'all' })}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <ArticleOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="All articles"
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                />
              </ListItemButton>
              <Divider />
              {visibleTree.map((n) => {
                const selected = scope.type !== 'all' && scope.type === n.type && scope.id === n.id
                const isCat = n.type === 'category'
                const count = countByNode.get(`${n.type}-${n.id}`)
                return (
                  <ListItemButton
                    key={`${n.type}-${n.id}`}
                    selected={selected}
                    onClick={() => setScope({ type: n.type, id: n.id })}
                    sx={{ pl: isCat ? 2 : 4 }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {isCat ? (
                        <AccountTreeOutlinedIcon fontSize="small" sx={{ color: 'text.primary' }} />
                      ) : (
                        <FolderOutlinedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={n.label}
                      primaryTypographyProps={{
                        variant: 'body2',
                        noWrap: true,
                        fontWeight: isCat ? 700 : 400,
                        color: isCat ? 'text.primary' : 'text.secondary',
                      }}
                    />
                    {count != null && (
                      <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }}>
                        {count}
                      </Typography>
                    )}
                  </ListItemButton>
                )
              })}
              {visibleTree.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No matches
                </Typography>
              )}
            </List>
          </Paper>

          {/* Right: scoped, searchable article list */}
          <Box sx={{ flexGrow: 1, minWidth: 0, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" component="div">
                {heading}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                · {shownArticles.length} article{shownArticles.length === 1 ? '' : 's'}
              </Typography>
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder="Search articles…"
              value={articleFilter}
              onChange={(e) => setArticleFilter(e.target.value)}
              sx={{ mb: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Paper variant="outlined">
              {shownArticles.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No articles match.
                </Typography>
              ) : (
                <List dense disablePadding sx={{ maxHeight: 520, overflow: 'auto' }}>
                  {shownArticles.map(articleRow)}
                </List>
              )}
            </Paper>
          </Box>
        </Box>
      </Box>
    </DataFetchWrapper>
  )
}

export default SolutionsBrowser
